import * as THREE from "three";
import type { GraphNode, GraphEdge } from "../types/graph";

/**
 * Configuration for the Level-of-Detail manager.
 */
export interface LODConfig {
  /** Distance threshold for full detail rendering (default: 200 units) */
  nearThreshold: number;
  /** Distance threshold for reduced detail rendering (default: 500 units) */
  midThreshold: number;
  /** Whether frustum culling is enabled */
  frustumCulling: boolean;
  /** Maximum number of nodes to load initially (default: 5000) */
  progressiveLoadLimit: number;
  /** Distance threshold for edge visibility (default: 300 units) */
  edgeDistanceThreshold: number;
}

/** LOD level for a node based on camera distance */
export type LODLevel = "full" | "reduced" | "point";

/** Function that resolves a node ID to its 3D position */
export type NodePositionLookup = (
  nodeId: string
) => THREE.Vector3 | undefined;

/**
 * Default LOD configuration values.
 */
export const DEFAULT_LOD_CONFIG: LODConfig = {
  nearThreshold: 200,
  midThreshold: 500,
  frustumCulling: true,
  progressiveLoadLimit: 5000,
  edgeDistanceThreshold: 300,
};

/**
 * Determines the LOD level for a node based on camera distance.
 *
 * - distance < nearThreshold (200) → "full" (full sphere with label)
 * - nearThreshold ≤ distance < midThreshold (500) → "reduced" (small sphere, no label)
 * - distance ≥ midThreshold (500) → "point" (single pixel, no label)
 */
export function getNodeLOD(
  cameraDistance: number,
  config: LODConfig = DEFAULT_LOD_CONFIG
): LODLevel {
  if (cameraDistance < config.nearThreshold) {
    return "full";
  }
  if (cameraDistance < config.midThreshold) {
    return "reduced";
  }
  return "point";
}

/**
 * Checks whether a 3D point is inside the given camera frustum.
 * Uses THREE.Frustum's containsPoint method.
 */
export function isInFrustum(
  position: THREE.Vector3,
  frustum: THREE.Frustum
): boolean {
  return frustum.containsPoint(position);
}

/**
 * Selects the initial subset of nodes for progressive loading.
 * Returns the top-K nodes sorted by fan_in in descending order.
 *
 * If the node list has fewer than `limit` nodes, all nodes are returned.
 */
export function getInitialNodeSubset(
  nodes: GraphNode[],
  limit: number
): GraphNode[] {
  if (nodes.length <= limit) {
    return [...nodes];
  }

  // Sort by fan_in descending and take the top K
  const sorted = [...nodes].sort((a, b) => b.fan_in - a.fan_in);
  return sorted.slice(0, limit);
}

/**
 * Determines whether an edge should be rendered based on the distance
 * of its endpoint nodes from the camera.
 *
 * An edge is rendered if at least one of its endpoints (source or target)
 * is within `edgeDistanceThreshold` (300 units) of the camera position.
 *
 * @param edge - The graph edge to evaluate
 * @param cameraPosition - Current camera position in 3D space
 * @param getNodePosition - Lookup function to resolve node IDs to positions
 * @param config - LOD configuration (uses edgeDistanceThreshold)
 */
export function shouldRenderEdge(
  edge: GraphEdge,
  cameraPosition: THREE.Vector3,
  getNodePosition: NodePositionLookup,
  config: LODConfig = DEFAULT_LOD_CONFIG
): boolean {
  const sourcePos = getNodePosition(edge.source);
  const targetPos = getNodePosition(edge.target);

  // If we can't resolve positions, don't render the edge
  if (!sourcePos && !targetPos) {
    return false;
  }

  const threshold = config.edgeDistanceThreshold;

  if (sourcePos && cameraPosition.distanceTo(sourcePos) < threshold) {
    return true;
  }

  if (targetPos && cameraPosition.distanceTo(targetPos) < threshold) {
    return true;
  }

  return false;
}

/**
 * The LODManager object bundles all LOD-related utilities.
 */
export const LODManager = {
  getNodeLOD,
  isInFrustum,
  getInitialNodeSubset,
  shouldRenderEdge,
};

export default LODManager;
